import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { ArrowLeft, Users, Plus, Loader2, Mail, Shield, Trash2, Clock, History, UserX } from 'lucide-react';

const ROLE_LABELS = { owner: 'Owner', admin: 'Admin', manager: 'Manager', recruiter: 'Recruiter', sales_rep: 'Sales Rep' };
const ACTION_LABELS = {
  'invite.sent': 'Invited a teammate', 'invite.revoked': 'Revoked an invite',
  'invite.accepted_new': 'Joined (new account)', 'invite.accepted_existing': 'Joined (existing account)',
  'member.role_changed': 'Changed a member role', 'member.deactivated': 'Removed a member',
};
const ASSIGNABLE_ROLES = ['admin', 'manager', 'recruiter', 'sales_rep'];

export default function AgencyTeam() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isOwner = user?.role === 'agency';
  const [data, setData] = useState(null);
  const [invites, setInvites] = useState([]);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [canManage, setCanManage] = useState(false);

  const load = useCallback(async () => {
    try {
      const m = await api.get('/agency/staff/members');
      setData(m.data);
      setCanManage(m.data.my_role === 'owner' || m.data.my_role === 'admin');
      if (m.data.my_role === 'owner' || m.data.my_role === 'admin') {
        const [i, a] = await Promise.all([
          api.get('/agency/staff/invites').catch(() => ({ data: [] })),
          api.get('/agency/staff/audit?limit=30').catch(() => ({ data: [] })),
        ]);
        setInvites(i.data || []); setAudit(a.data || []);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to load team'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const changeRole = async (member, role) => {
    try { await api.patch(`/agency/staff/members/${member.member_id}/role`, { role }); toast.success('Role updated'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not update role'); }
  };
  const deactivate = async (member) => {
    if (!window.confirm(`Remove ${member.name || member.email}? Their leads/tasks move to the owner.`)) return;
    try { await api.post(`/agency/staff/members/${member.member_id}/deactivate`); toast.success('Member removed'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not remove member'); }
  };
  const resend = async (inv) => { try { await api.post(`/agency/staff/invites/${inv.id}/resend`); toast.success('Invite resent'); } catch { toast.error('Failed'); } };
  const revoke = async (inv) => { try { await api.delete(`/agency/staff/invites/${inv.id}`); toast.success('Invite revoked'); load(); } catch { toast.error('Failed'); } };

  if (loading) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-600" /></div>;

  const seats = data?.seats || { used: 0, pending: 0, limit: 0 };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="agency-team-page">
      <div className="max-w-4xl mx-auto px-4 py-6">
        <Button variant="ghost" size="sm" onClick={() => navigate(isOwner ? '/agency/dashboard' : '/agency/crm')} data-testid="team-back" className="mb-3">
          <ArrowLeft className="w-4 h-4 mr-1" /> {isOwner ? 'Dashboard' : 'CRM'}
        </Button>
        <div className="flex items-center justify-between mb-1">
          <h1 className="text-2xl font-semibold flex items-center gap-2"><Users className="w-6 h-6 text-cyan-600" /> Team</h1>
          {canManage && (
            <Button onClick={() => setInviteOpen(true)} className="bg-cyan-600 hover:bg-cyan-700" data-testid="team-invite-btn">
              <Plus className="w-4 h-4 mr-1" /> Invite staff
            </Button>
          )}
        </div>
        <p className="text-sm text-slate-500 mb-5">
          {seats.used} of {seats.limit} seats used{seats.pending ? ` · ${seats.pending} pending` : ''}. Staff get their own login scoped to this workspace.
        </p>

        <Tabs defaultValue="members">
          <TabsList className="mb-4">
            <TabsTrigger value="members" data-testid="team-tab-members">Members</TabsTrigger>
            {canManage && <TabsTrigger value="invites" data-testid="team-tab-invites">Pending invites</TabsTrigger>}
            {canManage && <TabsTrigger value="activity" data-testid="team-tab-activity">Activity log</TabsTrigger>}
          </TabsList>

          <TabsContent value="members">
            <div className="space-y-2" data-testid="team-members-list">
              {(data?.members || []).map(m => (
                <Card key={m.member_id} className={!m.is_active ? 'opacity-50' : ''} data-testid={`team-member-${m.member_id}`}>
                  <CardContent className="p-3 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-800 truncate">{m.name || m.email}</div>
                      <div className="text-xs text-slate-500 truncate">{m.email}</div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {m.is_owner || !canManage || !m.is_active ? (
                        <Badge variant="secondary">{ROLE_LABELS[m.role] || m.role}</Badge>
                      ) : (
                        <Select value={m.role} onValueChange={(v) => changeRole(m, v)}>
                          <SelectTrigger className="h-8 w-32 text-xs" data-testid={`team-role-${m.member_id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {ASSIGNABLE_ROLES.map(r => <SelectItem key={r} value={r} className="text-xs">{ROLE_LABELS[r]}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      )}
                      {!m.is_active && <Badge variant="outline" className="text-slate-400">Removed</Badge>}
                      {canManage && !m.is_owner && m.is_active && (
                        <Button size="sm" variant="ghost" className="text-red-500 h-8" onClick={() => deactivate(m)} data-testid={`team-remove-${m.member_id}`}><UserX className="w-4 h-4" /></Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {canManage && (
            <TabsContent value="invites">
              {invites.length === 0 ? <p className="text-sm text-slate-400">No pending invites.</p> : (
                <div className="space-y-2" data-testid="team-invites-list">
                  {invites.map(inv => (
                    <Card key={inv.id} data-testid={`team-invite-${inv.id}`}>
                      <CardContent className="p-3 flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-sm text-slate-800 truncate flex items-center gap-2"><Mail className="w-3 h-3" />{inv.email}</div>
                          <div className="text-xs text-slate-500 flex items-center gap-2"><Shield className="w-3 h-3" />{ROLE_LABELS[inv.role] || inv.role} · <Clock className="w-3 h-3" />expires {inv.expires_at ? new Date(inv.expires_at).toLocaleDateString() : ''}</div>
                        </div>
                        <div className="flex gap-1 shrink-0">
                          <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => resend(inv)} data-testid={`team-resend-${inv.id}`}>Resend</Button>
                          <Button size="sm" variant="ghost" className="h-8 text-red-500" onClick={() => revoke(inv)} data-testid={`team-revoke-${inv.id}`}><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          )}

          {canManage && (
            <TabsContent value="activity">
              <div className="space-y-1" data-testid="team-audit-list">
                {audit.length === 0 ? <p className="text-sm text-slate-400">No activity yet.</p> : audit.map((a, i) => (
                  <div key={i} className="text-xs border-l-2 border-slate-200 pl-2 py-1">
                    <span className="text-slate-700 font-medium">{ACTION_LABELS[a.action] || a.action}</span>
                    <span className="text-slate-400"> · {a.actor_email || 'system'} · {a.created_at ? new Date(a.created_at).toLocaleString() : ''}</span>
                    {a.meta?.email && <span className="text-slate-400"> · {a.meta.email}</span>}
                  </div>
                ))}
              </div>
            </TabsContent>
          )}
        </Tabs>
      </div>
      <InviteDialog open={inviteOpen} onOpenChange={setInviteOpen} onSent={load} />
    </div>
  );
}

function InviteDialog({ open, onOpenChange, onSent }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('sales_rep');
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!email.trim()) return;
    setBusy(true);
    try {
      await api.post('/agency/staff/invite', { email: email.trim(), role });
      toast.success('Invitation sent');
      setEmail(''); setRole('sales_rep'); onOpenChange(false); onSent();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not send invite'); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="team-invite-dialog">
        <DialogHeader><DialogTitle>Invite a team member</DialogTitle>
          <DialogDescription>They'll get an email to join this workspace with their own login.</DialogDescription></DialogHeader>
        <Input placeholder="work email" value={email} onChange={e => setEmail(e.target.value)} data-testid="team-invite-email" />
        <Select value={role} onValueChange={setRole}>
          <SelectTrigger data-testid="team-invite-role"><SelectValue /></SelectTrigger>
          <SelectContent>
            {ASSIGNABLE_ROLES.map(r => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}
          </SelectContent>
        </Select>
        <p className="text-xs text-slate-400">Sales Rep & Recruiter: day-to-day CRM and recruiting. Manager: + transactions. Admin: + manage team & billing.</p>
        <DialogFooter>
          <Button onClick={send} disabled={busy} className="bg-cyan-600 hover:bg-cyan-700" data-testid="team-invite-send">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send invite'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
